import { Platform } from 'react-native';
import { storage as webStorage } from './firebaseConfig';
import { ref as webRef, uploadBytes, getDownloadURL as webGetDownloadURL } from 'firebase/storage';
import {
  getStorage as getNativeStorage,
  ref as nativeRef,
  getDownloadURL as nativeGetDownloadURL,
} from '@react-native-firebase/storage';
import * as ImageManipulator from 'expo-image-manipulator';

// ─────────────────────────────────────────────────────────────────────────
// SERVICIO DE STORAGE — Platform-aware.
// ─────────────────────────────────────────────────────────────────────────
// Antes este servicio usaba SÓLO el SDK web (`firebase/storage`) y forzaba
// a los callers a convertir URI → Blob con `fetch(uri).blob()`. Ese flujo
// es frágil en RN+Hermes (algunos devices Android devuelven blobs vacíos
// para URIs `content://` de la cámara, y el cliente HTTP del SDK web se
// cuelga sin error claro contra `firebasestorage.googleapis.com`).
//
// La API correcta en móvil es `storage().ref(path).putFile(localUri)` del
// SDK nativo, que recibe el path directamente sin pasar por blob.
//
// Esta nueva versión:
//   - Acepta un URI local (file://, content://, blob:, data:) en TODOS
//     los métodos públicos.
//   - En móvil usa @react-native-firebase/storage + putFile.
//   - En web mantiene el flujo fetch→blob→uploadBytes para soportar el
//     panel del owner Rent Car.
//   - Centraliza el path-building para que las reglas Firebase Storage
//     sigan funcionando idénticas (paths intactos).
// ─────────────────────────────────────────────────────────────────────────

const isRemoteUri = (uri: string) =>
  uri.startsWith('http://') || uri.startsWith('https://');

/** Sube un archivo (local o remoto) al path indicado y devuelve la URL pública. */
async function uploadLocalUri(path: string, uri: string): Promise<string> {
  if (Platform.OS === 'web') {
    // Web: fetch del URI (blob:/data:/http:) → Blob → uploadBytes.
    const response = await fetch(uri);
    const blob = await response.blob();
    const r = webRef(webStorage, path);
    const snapshot = await uploadBytes(r, blob);
    return webGetDownloadURL(snapshot.ref);
  }
  // Nativo — tres casos:
  //  - data:image/...;base64,XXXX  → putString(uri, 'data_url'). Es lo que
  //    mandan la cámara del KYC y la captura de perfil. Antes caía al
  //    putFile() de abajo, que espera una RUTA de archivo, no un data URI:
  //    fallaba siempre. Por eso esas subidas se habían desviado a Railway.
  //  - http(s)://  → fetch + put(blob).
  //  - file:// o content:// → putFile() directo, sin blob.
  const r = nativeRef(getNativeStorage(), path);
  if (uri.startsWith('data:')) {
    // @ts-ignore - putString existe en la instancia nativa; el tipo modular
    // no siempre lo expone.
    await r.putString(uri, 'data_url');
  } else if (isRemoteUri(uri)) {
    const response = await fetch(uri);
    const blob = await response.blob();
    // @ts-ignore - .put() existe en la instancia nativa pero el tipo modular
    // a veces no lo expone correctamente.
    await r.put(blob);
  } else {
    // putFile() acepta file:// y content:// directamente — NO le quitamos
    // el prefijo. Antes hacía `uri.replace('file://','')` y eso le pasaba
    // un path tipo `/data/user/0/...` que Android scoped storage bloquea
    // → "Hubo un problema subiendo el documento".
    await r.putFile(uri);
  }
  return nativeGetDownloadURL(r);
}

// ─────────────────────────────────────────────────────────────────────────
// AJUSTE PROFESIONAL DE IMÁGENES — resize + center-crop a un aspecto fijo.
// El dueño quiere que las fotos de vehículos y restaurantes queden
// organizadas y ajustadas al tamaño del banner (16:9), no subidas crudas.
// Esto normaliza resolución y aspecto (aunque el usuario se salte el recorte
// del ImagePicker) y comprime para bajar peso de Storage/CDN. Si el
// manipulador falla (web raro, URI exótica), cae a la imagen original.
//   - Portada/banner/vehículo → 16:9  (1280 x 720)
//   - Logo                    → 1:1   (512 x 512)
//   - Plato de menú           → 4:3   (900 x 675)
// ─────────────────────────────────────────────────────────────────────────
// Se EXPORTA (antes era privada) porque la selfie del KYC también tiene que
// pasar por aquí: `kyc-biometrics.tsx` la sube a /user_avatars, cuya regla de
// Storage tope en 3 MB, y expo-camera captura a la resolución completa del
// sensor. Sin ajustar, un móvil moderno produce una foto que la regla rechaza
// SIEMPRE y deja al usuario atrapado en la primera pantalla. Un ajustador
// duplicado allí habría sido una segunda verdad que mantener.
export async function resizeToAspect(
  uri: string,
  targetWidth: number,
  aspectW: number,
  aspectH: number
): Promise<string> {
  try {
    const targetHeight = Math.round((targetWidth * aspectH) / aspectW);
    // 1) Redimensionar a lo ancho objetivo (conserva el aspecto original).
    const resized = await ImageManipulator.manipulateAsync(
      uri,
      [{ resize: { width: targetWidth } }],
      { compress: 0.82, format: ImageManipulator.SaveFormat.JPEG }
    );
    // 2) Si quedó más alta que el objetivo, center-crop al aspecto exacto.
    if (resized.height > targetHeight + 2) {
      const originY = Math.max(0, Math.round((resized.height - targetHeight) / 2));
      const cropped = await ImageManipulator.manipulateAsync(
        resized.uri,
        [{ crop: { originX: 0, originY, width: targetWidth, height: targetHeight } }],
        { compress: 0.82, format: ImageManipulator.SaveFormat.JPEG }
      );
      return cropped.uri;
    }
    return resized.uri;
  } catch (e: any) {
    console.warn('[storage] resize falló, subo original:', e?.message);
    return uri;
  }
}

// CÓDIGO MUERTO ELIMINADO: `uploadKYCDocumentViaBackend` y `BACKEND_BASE`.
// Subían las fotos del KYC al backend de Railway, que fue borrado y
// respondía 404 en todas sus rutas — era el muro donde se estrellaba el
// revisor de Google Play ("Verificación Fallida" culpando a la foto del
// usuario cuando en realidad el servidor no existía).
// Ahora todo sube DIRECTO a Firebase Storage: es posible porque la Cloud
// Function `exchangeToken` garantiza sesión nativa incluso cuando el SDK
// nativo se cuelga en Play Integrity y el login cae al fallback REST.

export const storageService = {
  /**
   * Sube un documento KYC (cédula, licencia, etc.) DIRECTO a Firebase Storage.
   *
   * ANTES iba por el backend de Railway con Admin SDK. La razón era válida en
   * su momento: cuando el login caía al fallback REST no había sesión nativa,
   * y `putFile()` exige `request.auth.uid` — fallaba con unauthorized.
   *
   * AHORA ya no hace falta: la Cloud Function `exchangeToken` cambia el
   * idToken REST por un custom token y firma la sesión nativa al arrancar.
   * Con sesión nativa, la subida directa funciona y elimina un salto de red,
   * el límite de payload y — sobre todo — la dependencia de Railway, que fue
   * borrado y respondía 404 en todas sus rutas (era el muro donde se
   * estrellaba el revisor de Google Play).
   *
   * `base64` se sigue aceptando por compatibilidad de firma: si viene, se
   * arma un data URI, que `uploadLocalUri` sube con putString.
   *
   * Reglas aplicables (storage.rules): `/kyc_documents/{uid}/**` —
   * escribe solo el dueño, leen el dueño y los admins, máximo 10 MB.
   */
  async uploadKYCDocument(
    userId: string,
    documentType: string,
    uri: string,
    base64?: string | null
  ): Promise<string> {
    const path = `kyc_documents/${userId}/${documentType}_${Date.now()}.jpg`;
    // Preferimos el URI local (putFile es más eficiente que mandar base64).
    // Si el caller solo tiene base64, armamos el data URI.
    const fuente = uri && !uri.startsWith('data:')
      ? uri
      : base64
        ? `data:image/jpeg;base64,${base64.replace(/^data:[^,]+,/, '')}`
        : uri;
    return uploadLocalUri(path, fuente);
  },

  /** Foto de un vehículo del Marketplace — ajustada a banner 16:9. */
  async uploadVehicleImage(ownerId: string, vehicleId: string, uri: string): Promise<string> {
    const path = `vehicle_images/${ownerId}/${vehicleId}_${Date.now()}`;
    const fitted = await resizeToAspect(uri, 1280, 16, 9);
    return uploadLocalUri(path, fitted);
  },

  /** Logo (1:1) o portada/banner (16:9) del restaurante — ajustados. */
  async uploadRestaurantImage(
    ownerId: string,
    kind: 'logo' | 'cover' | 'storefront',
    uri: string
  ): Promise<string> {
    const path = `restaurant_images/${ownerId}/${kind}_${Date.now()}`;
    // storefront = foto en tiempo real del local (verificación) → 4:3 nítida.
    const fitted = kind === 'logo'
      ? await resizeToAspect(uri, 512, 1, 1)
      : kind === 'storefront'
      ? await resizeToAspect(uri, 1280, 4, 3)
      : await resizeToAspect(uri, 1280, 16, 9);
    return uploadLocalUri(path, fitted);
  },

  /** Comprobante de transferencia de un pedido. */
  async uploadOrderReceipt(orderId: string, customerId: string, uri: string): Promise<string> {
    const path = `receipts/${customerId}/${orderId}_${Date.now()}.jpg`;
    return uploadLocalUri(path, uri);
  },

  /**
   * Foto de perfil del usuario (avatar). Rutea por backend Admin SDK —
   * NO requiere sesión nativa Firebase en el cliente. Soporta:
   *   - URI remota (https://images.unsplash.com/... mock actual)
   *   - base64 (futuro, cuando la cámara real se implemente con
   *     ImagePicker base64:true)
   * Sube al path `user_avatars/{uid}/avatar_*.jpg` que sí está en
   * storage.rules (antes el cliente usaba `profile_photos/` que no
   * existía en las rules → deny + alert "problema al subir tu foto").
   * El backend también persiste profilePhoto en /users/{uid}.
   */
  async uploadProfilePhoto(userId: string, uri: string): Promise<string> {
    if (!userId) {
      throw new Error('Sesión no detectada. Cierra y vuelve a iniciar sesión.');
    }
    // Directo a Storage, igual que el KYC: la sesión nativa ya existe gracias
    // a `exchangeToken`, y el endpoint de Railway que se usaba antes está
    // muerto (404). Ahora acepta los tres formatos: data URI (cámara),
    // file:// (galería) y http(s) (avatar de Google).
    // Regla: `/user_avatars/{uid}/**` — escribe el dueño, máximo 3 MB.
    const path = `user_avatars/${userId}/avatar_${Date.now()}.jpg`;
    return uploadLocalUri(path, uri);
  },

  /**
   * Comprobante de la transferencia del chofer al dueño del rent car.
   *
   * La ruta lleva los DOS uids a propósito: las reglas de Storage no pueden
   * consultar Firestore, así que la única forma de dejar que el dueño LEA la
   * foto de otro es que su uid esté en el camino. Ver la nota en storage.rules.
   */
  async uploadRentReceipt(ownerId: string, uid: string, uri: string): Promise<string> {
    if (!ownerId || !uid) {
      throw new Error('Falta identificar al dueño o a tu cuenta. Vuelve a abrir el chat.');
    }
    const path = `rent_receipts/${ownerId}/${uid}/comprobante_${Date.now()}.jpg`;
    return uploadLocalUri(path, uri);
  },

  /** Fallback genérico — para callers que necesiten subir a un path custom. */
  async uploadToPath(path: string, uri: string): Promise<string> {
    return uploadLocalUri(path, uri);
  },
};
